import React, { useState, useEffect } from "react";
import EmailAnalysisAPI from "../../services/EmailAnalysisAPI";
import { useUser } from "../../context/UserContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faExclamationTriangle,
  faToggleOff,
} from "@fortawesome/free-solid-svg-icons";
import "../../css/Common.css";
import LoadingSpinner from "../../common/LoadingSpinner";
import "./css/EmailManualScan.css";

// Import sub-components
import SearchFilters from "./components/SearchFilters";
import AnalysisResults from "./components/AnalysisResults";
import AnalysisDetails from "./components/AnalysisDetails";

const EmailAnalysis = ({
  onToggleView,
  targetAnalysisKey,
  onTargetSelected,
}) => {
  const { loggedInUser } = useUser();
  const userUuid = loggedInUser?.uuid;

  // Core state
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);

  // Load all analyses on mount
  useEffect(() => {
    loadAllAnalyses();
  }, [userUuid]);

  // Scroll to analysis details when selectedAnalysis changes
  useEffect(() => {
    if (selectedAnalysis) {
      // Small delay to ensure the component has rendered
      const timer = setTimeout(() => {
        scrollToAnalysisDetails();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [selectedAnalysis]);

  // Auto-select analysis if targetAnalysisKey is provided (for notification navigation)
  useEffect(() => {
    if (targetAnalysisKey && analyses.length > 0) {
      console.log(
        "EmailAnalysis: Looking for target analysis with key:",
        targetAnalysisKey,
      );
      const targetAnalysis = analyses.find(
        (analysis) => analysis.key === targetAnalysisKey,
      );
      if (targetAnalysis) {
        console.log(
          "EmailAnalysis: Found target analysis, auto-selecting:",
          targetAnalysis,
        );
        setSelectedAnalysis(targetAnalysis);

        // Enhanced navigation to details - ensure user sees the details view
        const showDetailsFromNotification =
          localStorage.getItem("notificationShowDetails") === "true";
        console.log(
          "EmailAnalysis: Show details from notification flag:",
          showDetailsFromNotification,
        );

        // Always scroll to details when coming from notification, with multiple attempts
        const scrollAttempts = [100, 300, 500, 1000]; // Multiple timeouts to ensure success
        scrollAttempts.forEach((delay, index) => {
          setTimeout(() => {
            scrollToAnalysisDetails();
            console.log(
              `EmailAnalysis: Scroll attempt ${
                index + 1
              } - Scrolled to analysis details for notification navigation`,
            );
          }, delay);
        });

        // Clear the target after selection
        if (onTargetSelected) {
          onTargetSelected();
        }
      } else {
        console.log(
          "EmailAnalysis: Target analysis not found in current results, trying broader search",
        );
        // Fallback to broader search
        loadAnalysisForNotification(targetAnalysisKey);
      }
    }
  }, [targetAnalysisKey, analyses, onTargetSelected]);

  // Load specific analysis for notification if not found in recent results
  const loadAnalysisForNotification = async (analysisKey) => {
    try {
      console.log(
        "EmailAnalysis: Loading specific analysis for notification:",
        analysisKey,
      );
      // Try searching without date restrictions by bypassing default filters
      const results = await EmailAnalysisAPI.searchAnalyses({
        userUuid,
        bypassDefaultFilters: true,
      });
      const targetAnalysis = results.find(
        (analysis) => analysis.key === analysisKey,
      );

      if (targetAnalysis) {
        console.log(
          "EmailAnalysis: Found target analysis in broader search:",
          targetAnalysis,
        );
        // Add the found analysis to current analyses if not already present
        setAnalyses((prevAnalyses) => {
          const existing = prevAnalyses.find((a) => a.key === analysisKey);
          if (!existing) {
            return [targetAnalysis, ...prevAnalyses];
          }
          return prevAnalyses;
        });
        setSelectedAnalysis(targetAnalysis);

        // Multiple scroll attempts to ensure the detail view is reached
        const scrollAttempts = [200, 500, 800, 1200]; // Multiple timeouts to ensure success
        scrollAttempts.forEach((delay, index) => {
          setTimeout(() => {
            scrollToAnalysisDetails();
            console.log(
              `EmailAnalysis: Broader search scroll attempt ${
                index + 1
              } - Scrolled to analysis details`,
            );
          }, delay);
        });
      } else {
        console.warn(
          "EmailAnalysis: Target analysis not found even in broader search",
        );
        // Notify notification components so they can show the delete option
        window.dispatchEvent(
          new CustomEvent("email-analysis-not-found", {
            detail: { analysisKey },
          }),
        );
        setError("The requested email analysis could not be found.");
      }

      if (onTargetSelected) {
        onTargetSelected();
      }
    } catch (error) {
      console.error(
        "EmailAnalysis: Error loading analysis for notification:",
        error,
      );
      window.dispatchEvent(
        new CustomEvent("email-analysis-not-found", {
          detail: { analysisKey },
        }),
      );
      setError("Failed to load the requested email analysis.");
      if (onTargetSelected) {
        onTargetSelected();
      }
    }
  };

  // Core data loading function
  const loadAllAnalyses = async () => {
    if (!userUuid) {
      setError("User not authenticated");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log("EmailAnalysis: Loading all analyses for user:", userUuid);
      const results = await EmailAnalysisAPI.searchAnalyses({ userUuid });
      console.log("EmailAnalysis: Received results from API:", results);
      console.log("EmailAnalysis: Results length:", results?.length);
      console.log("EmailAnalysis: First result:", results?.[0]);

      setAnalyses(results || []);
    } catch (err) {
      console.error("Error loading analyses:", err);
      setError("Failed to load email analyses. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Data coordination handlers
  const handleSearchResults = (searchResults) => {
    console.log(
      "EmailAnalysis: handleSearchResults called with:",
      searchResults,
    );
    console.log("EmailAnalysis: Search results length:", searchResults?.length);
    setAnalyses(searchResults);
  };

  const handleAnalysisSelected = (analysis) => {
    setSelectedAnalysis(analysis);
    // Always scroll to details section with multiple attempts to ensure reliability
    const scrollAttempts = [50, 150, 300]; // Multiple timeouts to ensure success
    scrollAttempts.forEach((delay, index) => {
      setTimeout(() => {
        scrollToAnalysisDetails();
        console.log(
          `EmailAnalysis: Manual selection scroll attempt ${
            index + 1
          } - Scrolled to analysis details`,
        );
      }, delay);
    });
  };

  const handleAnalysisDeleted = (deletedKey) => {
    setAnalyses((prev) =>
      prev.filter((analysis) => analysis.key !== deletedKey),
    );
    if (selectedAnalysis && selectedAnalysis.key === deletedKey) {
      setSelectedAnalysis(null);
    }
  };

  // Navigation functions
  const scrollToAnalysisResults = () => {
    const element = document.querySelector(".analysis-results-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToAnalysisDetails = () => {
    const element = document.querySelector(".analysis-details-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      console.log(
        "EmailAnalysis: Successfully scrolled to analysis details section",
      );
    } else {
      console.warn(
        "EmailAnalysis: Could not find .analysis-details-section element for scrolling",
      );
      // Fallback: try alternative selectors
      const fallbackSelectors = [
        ".analysis-details",
        "[class*='analysis-details']",
        "[class*='details-section']",
      ];

      for (const selector of fallbackSelectors) {
        const fallbackElement = document.querySelector(selector);
        if (fallbackElement) {
          fallbackElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          console.log(
            `EmailAnalysis: Fallback scroll successful using selector: ${selector}`,
          );
          break;
        }
      }
    }
  };

  // Refresh function
  const refreshAnalyses = () => {
    loadAllAnalyses();
  };

  if (error) {
    return (
      <div className="common-container">
        <div className="common-section">
          <div className="common-section-content">
            <div className="error-message">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="fa-icon-margin-right-8"
              />
              {error}
              <div className="error-actions">
                <button onClick={refreshAnalyses} className="common-button">
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className="fa-icon-margin-right-8"
                  />
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="common-container">
      {/* Header Section */}
      <div className="common-section">
        <div className="common-section-header">
          <div className="section-header-content">
            <div className="section-header-left">
              <h2 className="email-analysis-header-title">
                <FontAwesomeIcon icon={faEnvelope} /> Emails Analyzer
              </h2>
              <p className="email-analysis-header-subtitle">
                View and manage your email scam detection analysis results.
                Forward your email to <strong>security@kinprotect.com</strong>{" "}
                for analysis.
              </p>
            </div>
            <div className="section-header-right">
              <button
                onClick={onToggleView}
                className="common-button toggle-button toggle-button-advanced"
                title="Switch to Simple View"
              >
                <FontAwesomeIcon
                  icon={faToggleOff}
                  className="fa-icon-margin-right-6"
                />
                Simple View
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="email-analysis-main-layout">
        {/* Left Column - Search Filters */}
        <div className="search-filters-column">
          <SearchFilters
            key="email-search-filters"
            userUuid={userUuid}
            onAnalysesUpdate={handleSearchResults}
            onLoadingChange={setLoading}
            onError={setError}
          />
        </div>

        {/* Right Column - Analysis Results and Details */}
        <div className="analysis-content-column">
          {/* Show loading indicator when loading */}
          {loading && (
            <div
              className="loading-message"
              style={{
                padding: "20px",
                textAlign: "center",
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "8px",
                margin: "20px 0",
              }}
            >
              <LoadingSpinner
                size="sm"
                message="Searching email analyses..."
                className="compact"
              />
            </div>
          )}

          {/* Analysis Results Section */}
          {console.log(
            "EmailAnalysis: Rendering with analyses:",
            analyses,
            "Length:",
            analyses?.length,
          )}
          <AnalysisResults
            rawAnalyses={analyses}
            selectedAnalysis={selectedAnalysis}
            onAnalysisClick={handleAnalysisSelected}
            onRefresh={refreshAnalyses}
            userUuid={userUuid}
            onError={setError}
          />

          {/* Analysis Details Section */}
          {selectedAnalysis && (
            <AnalysisDetails
              selectedAnalysis={selectedAnalysis}
              onScrollToAnalysisResults={scrollToAnalysisResults}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailAnalysis;
